import { PortableText } from '@portabletext/react';
import { useMemo } from 'react';
import type { TypedObject } from 'sanity';

import { Captions } from '~components/Typography';
import { routes } from '~constants/routes';
import { annals } from '~features/annals/hooks';
import type { UnsubscriptionFlowStages } from '~features/annals/names';
import { useTranslations } from '~i18n/hooks';
import { BlockLink } from '~shared/api/sanity/ui/BlockLink';
import { components } from '~shared/api/sanity/utils/portableTextRender';

const useCustomPortableTextComponents = (screen: UnsubscriptionFlowStages) => {
	const clickSupport =
		annals.subscription.useClickLinkContactSupportCancelSub(screen);
	const clickRefund =
		annals.subscription.useClickLinkRefundPolicyCancelSub(screen);

	return useMemo(
		() => ({
			...components,
			marks: {
				...components.marks,
				link: ({
					children,
					value,
				}: {
					children: React.ReactNode;
					value?: { href: string } & TypedObject;
				}) => {
					const handleClick = (href?: string) => {
						if (href === routes.refund.home) {
							clickRefund();
						} else if (href === routes.support.home) {
							clickSupport();
						}
					};

					return (
						<BlockLink value={value} onClick={handleClick}>
							{children}
						</BlockLink>
					);
				},
			},
		}),
		[clickSupport, clickRefund]
	);
};

export const Policies = ({ screen }: { screen: UnsubscriptionFlowStages }) => {
	const t = useTranslations('MySubscriptions');
	const customComponents = useCustomPortableTextComponents(screen);

	return (
		<Captions size="L" color="white-l" as="div" underline>
			<PortableText // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				value={t.raw('policies')}
				components={customComponents}
			/>
		</Captions>
	);
};
